import { createContext, useContext, useState, useEffect } from "react";
import { logger } from '../../utils/logger';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Clean up extra localStorage items to match production
const cleanupLocalStorage = () => {
  const allowedKeys = ['access_token', 'refresh_token', 'username', 'clientCode', 'permissions'];
  const allKeys = Object.keys(localStorage);
  
  allKeys.forEach(key => {
    if (!allowedKeys.includes(key)) {
      localStorage.removeItem(key);
      logger.log(`Removed extra localStorage item: ${key}`);
    }
  });
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem("access_token"));

  useEffect(() => {
    // Clean up on mount
    cleanupLocalStorage();
    
    const handler = () => setIsAuthenticated(!!localStorage.getItem("access_token"));
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const login = (token: string) => {
    localStorage.setItem("access_token", token);
    setIsAuthenticated(true);
  };
  const logout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("clientCode");
    localStorage.removeItem("username");
    localStorage.removeItem("permissions");
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
} 